import React, { useEffect, useState } from "react";
import { Filter, Loader2 } from "lucide-react";
import api from "@/lib/api";

const RANGES = [["7", "7 أيام"], ["30", "30 يومًا"], ["90", "90 يومًا"]];

/** قمع التحويل: زيارة → ضغط ابدأ → فتح التسجيل → إكمال → أول مشروع (جلسات فريدة) + سلسلة أسبوعية */
export const AdminFunnel = () => {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.get(`/admin/funnel?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setBusy(false));
  }, [days]);
  const max = Math.max(1, ...(data?.steps || []).map((s) => s.count));
  const weakest = (data?.steps || []).slice(1).reduce((w, s) => (w === null || s.rate < w.rate ? s : w), null);
  return (
    <div className="glass rounded-2xl border border-border p-4" data-testid="admin-funnel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Filter className="w-4 h-4 text-primary" /><h3 className="font-bold text-sm">قمع التحويل — أين يتسرّب الزوار؟</h3>
        <div className="ms-auto flex gap-1">
          {RANGES.map(([v, l]) => <button key={v} type="button" onClick={() => setDays(v)} className={`text-xs px-2.5 py-1 rounded-full border ${days === v ? "bg-primary text-white border-primary" : "border-border text-muted-foreground"}`} data-testid={`funnel-range-${v}`}>{l}</button>)}
        </div>
      </div>
      {busy && !data ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : !data ? <p className="text-xs text-muted-foreground">لا بيانات بعد</p> : (
        <>
          <div className="space-y-2">
            {data.steps.map((s, i) => (
              <div key={s.id} data-testid={`funnel-step-${s.id}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold">{s.label}</span>
                  <span className="text-muted-foreground"><b className="text-foreground">{s.count}</b>{i > 0 && <> · {s.rate}% من الخطوة السابقة</>}</span>
                </div>
                <div className="h-2.5 rounded-full bg-secondary overflow-hidden"><div className={`h-full rounded-full ${i === 0 ? "bg-primary" : s.rate < 10 ? "bg-destructive" : s.rate < 30 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.max(2, (100 * s.count) / max)}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            <span>جرّبوا بدون تسجيل: <b className="text-foreground">{data.tries}</b></span>
            {weakest && <span>أضعف خطوة: <b className="text-destructive">{weakest.label} ({weakest.rate}%)</b></span>}
          </div>
          {data.alerts?.map((a, i) => <div key={i} className="mt-3 rounded-xl border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs font-bold text-destructive" data-testid={`funnel-alert-${i}`}>⚠ {a}</div>)}
          {data.note && <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed" data-testid="funnel-note">ℹ {data.note}</p>}
          <div className="grid md:grid-cols-3 gap-3 mt-4" data-testid="funnel-stages">
            {[["التسجيل — مرحلة بمرحلة", data.registration?.stages, "reg"], ["التوليد — من المشروع إلى النتيجة", data.generation?.stages, "gen"], ["التجربة بدون تسجيل", data.guest?.stages, "guest"]].map(([title, stages, key]) => (
              <div key={key} className="rounded-xl border border-border/60 p-3" data-testid={`funnel-stages-${key}`}>
                <div className="text-xs font-bold mb-2">{title}</div>
                {(stages || []).map((st) => (
                  <div key={st.id} className="flex items-center justify-between text-[11px] py-1 border-t border-border/40 first:border-0">
                    <span>{st.label}</span><span><b>{st.count}</b>{st.rate ? <span className="text-muted-foreground"> · {st.rate}%</span> : null}</span>
                  </div>
                ))}
                {key === "gen" && <div className="flex items-center justify-between text-[11px] py-1 border-t border-border/40 text-destructive"><span>فشل التوليد</span><b>{data.generation?.failed || 0}</b></div>}
                {key === "gen" && data.generation?.fail_reasons?.map((r) => <div key={r.k} className="text-[10px] text-muted-foreground truncate">• {r.k} ({r.n})</div>)}
                {key === "reg" && <div className="text-[10px] text-muted-foreground mt-1">عبر Google: {data.registration?.by_provider?.google || 0} · عبر البريد: {data.registration?.by_provider?.email || 0}</div>}
                {key === "reg" && data.registration?.devices?.length > 0 && <div className="text-[10px] text-muted-foreground mt-1">فتح التسجيل حسب الجهاز: {data.registration.devices.map((d) => `${d.k} ${d.n}`).join(" · ")}</div>}
                {key === "reg" && data.registration?.errors?.length > 0 && (
                  <div className="mt-1" data-testid="funnel-reg-errors"><div className="text-[10px] font-bold text-destructive">أخطاء التسجيل:</div>
                    {data.registration.errors.map((e, i) => <div key={i} className="text-[10px] text-muted-foreground">• {e.err} — {e.provider || "?"} — {e.device}{e.in_app ? ` / ${e.in_app}` : ""} ({e.count})</div>)}
                  </div>
                )}
              </div>
            ))}
          </div>
          {data.series?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="funnel-weekly">
                <thead><tr className="text-muted-foreground"><th className="text-start py-1">الأسبوع</th>{data.steps.map((s) => <th key={s.id} className="py-1">{s.label}</th>)}</tr></thead>
                <tbody>{data.series.slice(-8).map((w) => <tr key={w.week} className="border-t border-border/50"><td className="py-1 font-mono">{w.week}</td>{data.steps.map((s) => <td key={s.id} className="py-1 text-center">{w[s.id]}</td>)}</tr>)}</tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
